/**
 * @file 预约处理器 Hook
 * @description 处理预约按钮点击，包含埋点和跳转逻辑
 */

import { useCallback } from "react";
import { goToNativePage } from "@/utils/common";
import { trackGdp } from "@/utils/gio-report";
import { getReportVipData, entry } from "../../../utils";
import { StationItemData } from "../components/StationList";
import { UserLevelInfo } from "../../../types";
import { getVipType } from "../utils/bookingUtils";

/**
 * 预约处理器配置接口 - VIP会员页专用
 */
export interface BookingHandlerConfig {
  versionId?: string;
  componentCode?: string;
  levelInfo?: UserLevelInfo | null;
  userType?: string;
}

export interface BookingHandlerResult {
  handleBookingClick: (station: StationItemData) => void;
}

/**
 * 预约处理器 Hook - VIP会员页专用
 */
const useBookingHandler = (config: BookingHandlerConfig = {}): BookingHandlerResult => {
  const { versionId = "", componentCode = "", levelInfo, userType = "" } = config;

  /**
   * 处理预约按钮点击
   * 1. 上报埋点 B037
   * 2. 跳转到场站详情页并打开桩列表弹窗
   * @param station - 充电站数据
   */
  const handleBookingClick = useCallback(
    (station: StationItemData) => {
      if (!station || !station.stationId) {
        console.error("无效的充电站数据");
        return;
      }

      // 埋点追踪 - B037
      if (levelInfo) {
        const vipData = getReportVipData(levelInfo) as {
          vip_level: string;
          vip_protection: string;
          vip_validity: string;
        };
        trackGdp("P11222", "B037", {
          versionId,
          ComponentCode: componentCode,
          var_ch: entry(),
          userType,
          vip_type: getVipType(levelInfo?.memberTypeSeg, levelInfo?.member),
          vip_level: vipData.vip_level,
          vip_protection: vipData.vip_protection,
          vip_validity: vipData.vip_validity,
        });
      }

      // 跳转场站详情页并调起桩列表弹窗
      const { origin } = window.location;
      const stationId = station.stationId;

      goToNativePage({
        app: `xiaopeng://charging/station/detail?id=${stationId}&isEquipListOpen=true`,
        mini: `/charging/pages/station-detail/index?stationId=${stationId}&isEquipListOpen=true`,
        browser: `${origin}/apps/charge-web/jump-to-wx-mini.html?page=charging/pages/station-detail/index&query=stationId%3D${stationId}%26isEquipListOpen%3Dtrue`,
      });
    },
    [versionId, componentCode, levelInfo, userType]
  );

  return {
    handleBookingClick,
  };
};

export default useBookingHandler;
